import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from "@angular/core";
import { OnDestroy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Router } from "@angular/router";
import { environment } from "environments/environment";
import { Subject, Subscription } from "rxjs";
import { wysiwygField } from "../../quote/quote-mail-editor/wysiwyg-field.const";
import { User } from "../../user/user.model";
import { UserService } from "../../user/user.service";
import { noop } from "../../shared/noop.function";
import { StripHtmlTagsPipe } from "../../shared/html/strip-html-tags.pipe";
import { placeholder } from "../wysiwyg/config/placeholder.const";
import { WysiwygComponent } from "../wysiwyg/wysiwyg.component";
import { UploadComponent } from "../upload/upload.component";

@Component({
  selector: "app-email-editor",
  templateUrl: "./email-editor.component.html",
  styleUrls: ["./email-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    WysiwygComponent,
    UploadComponent,
  ],
})
export class EmailEditorComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private userService = inject(UserService);
  private router = inject(Router);

  public activeEditorIndex = 1;
  public form: UntypedFormGroup;
  public wysiwygField = wysiwygField;
  public messageConfig: object = {};
  public wysiwygChange = new Subject();
  public wysiwygText: string;
  public user: User = null;
  public uploadPath = `${environment.apiUrl}/upload`;
  private subscription = new Subscription();

  ngOnInit() {
    this.setViewOptions();

    this.userService.getUser().then((user) => {
      this.user = user;
    }, noop);

    this.createForm();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.wysiwygChange.unsubscribe();
  }

  public isOpened(): boolean {
    return this.activeEditorIndex === 0;
  }

  public open() {
    this.activeEditorIndex = 0;
  }

  public close() {
    this.activeEditorIndex = 1;
  }

  public wrapPlaceholder(placeholder: string, startTag = "[[", endTag = "]]") {
    return startTag + placeholder + endTag;
  }

  public setField(fieldKey: string, value: string) {
    this.form.get(fieldKey).setValue(value);
    this.form.updateValueAndValidity();
  }

  public setAttachment(url: string) {
    this.form.patchValue({ attachment: url });
  }

  private createForm() {
    this.form = this.formBuilder.group({
      subject: ["", Validators.required],
      message: ["", Validators.required],
      attachment: [""],
    });

    this.observeFormChanges();
  }

  private observeFormChanges() {
    this.subscription.add(
      this.form.valueChanges
        .pipe(distinctUntilChanged(), debounceTime(1000))
        .subscribe((change) => {
          this.form.updateValueAndValidity();
        }),
    );

    this.subscription.add(
      this.wysiwygChange.subscribe(
        (event: { field: string; value: string }) => {
          let cleanValue = event.value;

          if (event.field === wysiwygField.subject) {
            cleanValue = new StripHtmlTagsPipe().transform(event.value);
          }

          this.setField(event.field, cleanValue);
        },
      ),
    );
  }

  private setViewOptions() {
    this.wysiwygText =
      "<br/><br/>" + this.wrapPlaceholder(placeholder.mySignature);
  }
}
